import React from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { DISPOSITION_OPTIONS } from './constants';

// Search box + status/disposition/date dropdowns. Pure controlled component;
// the container owns the filter state and debounces the search via the value it
// passes back in.
export default function CaseFilters({
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    dispositionFilter,
    setDispositionFilter,
    dateFilter,
    setDateFilter,
    uniqueDispositions,
    filteredCount,
    totalCount
}) {
    const hasFilters =
        searchQuery || statusFilter !== 'all' || dispositionFilter !== 'all' || dateFilter !== 'all';

    const clearFilters = () => {
        setSearchQuery('');
        setStatusFilter('all');
        setDispositionFilter('all');
        setDateFilter('all');
    };

    return (
        <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="flex flex-col lg:flex-row gap-4">
                <div className="flex-1">
                    <div className="relative">
                        <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by Case ID, ASIN, FNSKU, tracking, product name..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        {searchQuery && (
                            <button
                                type="button"
                                onClick={() => setSearchQuery('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <XMarkIcon className="h-5 w-5" />
                            </button>
                        )}
                    </div>
                </div>

                <div className="flex flex-wrap gap-3">
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                        <option value="all">All Statuses</option>
                        {DISPOSITION_OPTIONS.map((status) => (
                            <option key={status} value={status}>{status}</option>
                        ))}
                    </select>

                    <select
                        value={dispositionFilter}
                        onChange={(e) => setDispositionFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                        <option value="all">All Dispositions</option>
                        {uniqueDispositions.map((disposition) => (
                            <option key={disposition} value={disposition}>{disposition}</option>
                        ))}
                    </select>

                    <select
                        value={dateFilter}
                        onChange={(e) => setDateFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                        <option value="all">All Time</option>
                        <option value="today">Today</option>
                        <option value="week">Last 7 Days</option>
                        <option value="month">Last 30 Days</option>
                    </select>

                    {hasFilters && (
                        <button
                            type="button"
                            onClick={clearFilters}
                            className="px-3 py-2 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                        >
                            Clear Filters
                        </button>
                    )}
                </div>
            </div>

            <div className="mt-3 text-sm text-gray-600">
                Showing {filteredCount} of {totalCount} cases
                {hasFilters && <span className="ml-2 text-blue-600">(filtered)</span>}
            </div>
        </div>
    );
}
